/**
 * wfBindingsApi — typed client for the Approval Setup screen.
 *
 * Two independent bindings, deliberately kept separate because they answer
 * different questions:
 *   1. WHAT needs approval — a business process is bound to a workflow (t610).
 *   2. WHO approves it     — each approval level of that workflow is bound to
 *                            a login id and/or a role (t046).
 *
 * Like the rest of the `v1/wf/*` facade, the tenant and the acting user are
 * derived server-side from the verified JWT, so nothing here ever sends a
 * tenantId or an actorUserId from the browser.
 */
import { apiRequest } from './client.js';

type Tok = string | null | undefined;

/** A bindable business process, with its current binding if it has one. */
export interface ProcessBindingRow {
  readonly processCode: string;
  readonly moduleCode: string;
  readonly moduleLabelKey: string;
  readonly entityType: string;
  readonly labelKey: string;
  readonly descriptionKey: string;
  readonly workflowCode: string | null;
  readonly isEnabled: boolean;
  readonly bindingId: string | null;
}

/** One approval level of the bound workflow. */
export interface ApprovalLevel {
  readonly stepId: string;
  readonly approvalLevel: number;
  readonly stepName: string;
  readonly stepType: string;
  readonly approverType: string | null;
  readonly approverUserId: string | null;
  readonly approverRoleCode: string | null;
  readonly hierarchyLevelOffset: number | null;
  readonly isMandatory: boolean;
  readonly allowDelegate: boolean;
  readonly onApprovalAction: string;
  readonly approvalGotoStep: number | null;
  readonly onRejectionAction: string;
  readonly rejectionGotoStep: number | null;
  readonly alternateWorkflowCode: string | null;
}

/** Where an APPROVE decision goes. Must match the t046 CHECK constraint. */
export const APPROVAL_ACTIONS = ['NEXT_LEVEL', 'GOTO_STEP', 'COMPLETE'] as const;

/** Where a REJECT decision goes. Must match the t046 CHECK constraint. */
export const REJECTION_ACTIONS = [
  'RETURN_TO_PREVIOUS',
  'RETURN_TO_STEP',
  'RETURN_TO_CREATOR',
  'TERMINATE',
  'TRIGGER_ALTERNATE',
] as const;

export interface LevelsResponse {
  readonly workflowCode: string | null;
  readonly workflowDefinitionId: string | null;
  readonly levels: readonly ApprovalLevel[];
}

export interface ApproverDirectory {
  readonly users: ReadonlyArray<{
    readonly userId: string;
    readonly displayName: string;
    readonly employeeCode: string | null;
  }>;
  readonly roles: ReadonlyArray<{
    readonly roleCode: string;
    readonly roleName: string;
  }>;
}

/** Everything the level form can set. Omitted keys are left untouched. */
export interface LevelApproverInput {
  readonly approverType?: string | null;
  readonly approverUserId?: string | null;
  readonly approverRoleCode?: string | null;
  readonly hierarchyLevelOffset?: number | null;
  readonly isMandatory?: boolean;
  readonly allowDelegate?: boolean;
  readonly onApprovalAction?: string | null;
  readonly approvalGotoStep?: number | null;
  readonly onRejectionAction?: string | null;
  readonly rejectionGotoStep?: number | null;
  readonly alternateWorkflowCode?: string | null;
}

/** The approver strategies the engine's resolver actually implements. */
export const APPROVER_TYPES = [
  'SPECIFIC_USER',
  'ROLE_BASED',
  'HIERARCHY',
  'TERRITORY_HEAD',
  'DYNAMIC_RULE',
  'INITIATOR',
] as const;

const BASE = '/v1/wf/bindings';

// ── 1. Process → Workflow ────────────────────────────────────────────────────

export const listProcessBindings = (token: Tok) =>
  apiRequest<{ processes: ProcessBindingRow[] }>(BASE, { token });

export const saveProcessBinding = (
  token: Tok,
  processCode: string,
  body: { workflowCode: string; isEnabled?: boolean },
) => apiRequest<unknown>(`${BASE}/${encodeURIComponent(processCode)}`, {
  method: 'PUT', token, body,
});

export const removeProcessBinding = (token: Tok, processCode: string) =>
  apiRequest<{ removed: number }>(`${BASE}/${encodeURIComponent(processCode)}`, {
    method: 'DELETE', token,
  });

// ── 2. Level → Approver ──────────────────────────────────────────────────────

export const listApprovalLevels = (token: Tok, processCode: string) =>
  apiRequest<LevelsResponse>(`${BASE}/${encodeURIComponent(processCode)}/levels`, { token });

/**
 * Saves every level in one call. The server rejects the whole payload if any
 * level of the bound workflow is missing or has no usable approver, so a
 * half-configured chain can never be stored.
 *
 * There is deliberately no add/remove: how many levels exist is decided by the
 * workflow definition, not by this screen.
 */
export const saveApprovalLevels = (
  token: Tok,
  processCode: string,
  levels: ReadonlyArray<LevelApproverInput & { stepId: string }>,
) => apiRequest<{ levels: ApprovalLevel[] }>(
  `${BASE}/${encodeURIComponent(processCode)}/levels`,
  { method: 'PUT', token, body: { levels } },
);

// ── Pickers ──────────────────────────────────────────────────────────────────

export const getApproverDirectory = (token: Tok) =>
  apiRequest<ApproverDirectory>(`${BASE}/approver-directory`, { token });
